// Anthology: relabel Color option text based on the selected Base Metal tier,
// and keep the collapsed-step legend's swatch dot in sync with the selected
// Color value. Display-only - does not touch the underlying option value
// used for variant matching/cart/checkout.
//
// Rewritten 2026-09 for Canopy 7.4's native <variant-picker> component, which
// renders each option as EITHER a "listed" fieldset of radio buttons OR a
// "dropdown" <custom-select> element, depending on the block's selector_style
// setting - both are handled here.
//
// variant-picker.js replaces the whole option-selector DOM after a change
// (it re-fetches the section HTML for the new variant), so relabeling on
// "change" alone gets wiped by that swap. Relabeling only afterwards, on the
// component's own "on:variant:change", left visible flicker: the legend said
// "Gold" (raw) from the click until the swap, and the fresh nodes said "Gold"
// again until this ran. So it also relabels immediately after the click
// (document-level change runs after the picker's own handler), relabels the
// incoming nodes in on:variant:before-replace-element (before they are
// inserted), and watches the legend text itself: the picker resets it to the
// raw value a moment after inserting the new nodes (updateSwatchLabel), and
// the observer puts the friendly label back before the browser paints.
(function () {
  if (window.__anthologyColorLabels) return;
  window.__anthologyColorLabels = true;

  var LABELS = {
    brass:    { GOLD: '14K Gold Plated', SILVER: '14K White Gold Plated', 'ROSE GOLD': '14K Rose Gold Plated' },
    sterling: { GOLD: '14K Gold Plated', SILVER: 'Silver',                'ROSE GOLD': '14K Rose Gold Plated' },
    gold14k:  { GOLD: '14K Yellow Gold', SILVER: '14K White Gold',        'ROSE GOLD': '14K Rose Gold' },
    gold10k:  { GOLD: '10K Yellow Gold', SILVER: '10K White Gold',        'ROSE GOLD': '10K Rose Gold' },
    tungsten: { GOLD: '14K Gold Plated', SILVER: '14K White Gold Plated', 'ROSE GOLD': '14K Rose Gold Plated' }
  };

  var METAL_OPTION_NAMES = ['Base Metal', 'Material', 'Metal'];
  var COLOR_OPTION_NAME = 'Color';

  function metalTier(raw) {
    var v = (raw || '').toUpperCase();
    if (v.indexOf('BRASS') > -1) return 'brass';
    if (v.indexOf('STERLING') > -1) return 'sterling';
    if (v.indexOf('TUNGSTEN') > -1) return 'tungsten';
    if (v.indexOf('10K') > -1) return 'gold10k';
    if (v.indexOf('14K') > -1) return 'gold14k';
    return null;
  }

  function colorKey(raw) {
    var v = (raw || '').trim().toUpperCase();
    if (v.indexOf('ROSE') > -1) return 'ROSE GOLD';
    if (v === 'SILVER') return 'SILVER';
    if (v === 'GOLD') return 'GOLD';
    return null;
  }

  function findOptionRoot(picker, names) {
    for (var i = 0; i < names.length; i++) {
      var el = picker.querySelector('[data-option="' + names[i] + '"]');
      if (el) return el;
    }
    return null;
  }

  function getSelectedValue(root) {
    var checkedInput = root.querySelector('input.opt-btn:checked');
    if (checkedInput) return checkedInput.value;

    var selectedOption = root.querySelector('.custom-select__option[aria-selected="true"]');
    if (selectedOption) return selectedOption.dataset.value;

    return null;
  }

  function getOptionEntries(root) {
    var entries = [];

    root.querySelectorAll('input.opt-btn').forEach(function (input) {
      var label = root.querySelector('label[for="' + input.id + '"]');
      if (!label) return;
      var span = label.querySelector('span:not(.visually-hidden)') || label.querySelector('span');
      if (!span) return;
      entries.push({
        value: input.value,
        isSelected: input.checked,
        setText: function (text) { span.textContent = text; }
      });
    });

    root.querySelectorAll('.custom-select__option[data-value]').forEach(function (li) {
      var span = li.querySelector('span.pointer-events-none');
      if (!span) return;
      var isSelected = li.getAttribute('aria-selected') === 'true';
      entries.push({
        value: li.dataset.value,
        isSelected: isSelected,
        setText: function (text) {
          span.textContent = text;
          if (isSelected) {
            var customSelect = li.closest('custom-select');
            var buttonSpan = customSelect && customSelect.querySelector('.custom-select__btn > span');
            if (buttonSpan) buttonSpan.textContent = text;
          }
        }
      });
    });

    return entries;
  }

  function relabel(picker) {
    var metalRoot = findOptionRoot(picker, METAL_OPTION_NAMES);
    var colorRoot = findOptionRoot(picker, [COLOR_OPTION_NAME]);
    if (!colorRoot) return;

    // The collapsed-step swatch dot ("2. COLOR: [dot] Gold") tracks the raw
    // selected value (matching settings.swatch_colors keys) regardless of
    // whether metal-tier relabeling below applies - update it unconditionally.
    var dot = colorRoot.querySelector('.option-selector__label-swatch');
    var rawSelectedValue = getSelectedValue(colorRoot);
    if (dot) {
      dot.setAttribute('data-swatch', (rawSelectedValue || '').replace(/"/g, '').toLowerCase());
    }

    if (!metalRoot) return;

    var tier = metalTier(getSelectedValue(metalRoot));
    if (!tier) return; // unrecognized metal system (e.g. Tungsten) - leave labels as-is

    var map = LABELS[tier];

    getOptionEntries(colorRoot).forEach(function (entry) {
      var key = colorKey(entry.value);
      if (key && map[key]) entry.setText(map[key]);
    });

    // The collapsed-step summary / dropdown legend text ("2. COLOR: Gold") is
    // a separate element that variant-picker.js repopulates from the raw
    // option value on every update - relabel it too, and re-derive it from
    // the currently selected value each time so it never leaks a stale label
    // across metal changes. Only written when it differs: the legend text is
    // watched below, so an unconditional write would retrigger the watcher.
    var legend = colorRoot.querySelector('.js-color-text');
    if (legend) {
      var selectedKey = colorKey(rawSelectedValue);
      var legendText = (selectedKey && map[selectedKey]) || rawSelectedValue || '';
      if (legend.textContent !== legendText) legend.textContent = legendText;
    }
  }

  function relabelAll() {
    document.querySelectorAll('variant-picker').forEach(relabel);
  }

  function init() {
    relabelAll();

    document.addEventListener('on:variant:change', relabelAll);
    document.addEventListener('shopify:section:load', relabelAll);

    // Right after a click: the picker's own change handler has just reset the
    // legend to the raw value; put the friendly label straight back.
    document.addEventListener('change', function (evt) {
      var picker = evt.target.closest && evt.target.closest('variant-picker');
      if (picker) relabel(picker);
    });

    // Freshly rendered picker content, before it is inserted into the page.
    document.addEventListener('on:variant:before-replace-element', function (evt) {
      var replaceWith = evt.detail && evt.detail.replaceWith;
      if (!replaceWith || !replaceWith.querySelectorAll) return;
      if (replaceWith.matches && replaceWith.matches('variant-picker')) relabel(replaceWith);
      replaceWith.querySelectorAll('variant-picker').forEach(relabel);
    });

    // The picker resets the legend to the raw value after inserting new nodes;
    // relabel as soon as that happens (microtask, so before the next paint).
    new MutationObserver(function (records) {
      var pickers = [];
      records.forEach(function (r) {
        var node = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        if (!node || !node.closest || !node.closest('.js-color-text')) return;
        var picker = node.closest('variant-picker');
        if (picker && pickers.indexOf(picker) === -1) pickers.push(picker);
      });
      pickers.forEach(relabel);
    }).observe(document.body, { childList: true, characterData: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
